import { z, type ZodRawShape, type ZodTypeAny } from "zod";

import { ACCOUNT_DEPLOY_REQUIRED_ENV } from "@/lib/account-deploy/required-env";

/** The variable whose presence marks a process as an isolated account deploy. */
export const ACCOUNT_DEPLOY_MARKER = "AGENT_IDE_ACCOUNT";

const REQUIRED_IN_ACCOUNT_DEPLOY = new Set(ACCOUNT_DEPLOY_REQUIRED_ENV.map(entry => entry.name));

/** True when this process is an account deploy (docs/architecture/agent-ide-account-deploy.md). */
export function isAccountDeploy(env: NodeJS.ProcessEnv = process.env): boolean {
  return !!env[ACCOUNT_DEPLOY_MARKER]?.trim();
}

/**
 * Derive an account deploy's env schema from the shared one.
 *
 * The profile requires exactly ACCOUNT_DEPLOY_REQUIRED_ENV: a shared-required field outside that
 * list becomes optional, a listed field the shared schema left optional becomes required. Every
 * other field, defaults and transforms included, is the declaration as written, so a value the
 * shared deploy would default or coerce is defaulted or coerced here too. Only the parse
 * differs: the type the caller exports stays the shared one, and a field outside the profile is
 * undefined at runtime behind it, by design (section 8 of the doc).
 */
export function accountDeployProfile<Shape extends ZodRawShape>(
  schema: z.ZodObject<Shape>
): z.ZodObject<ZodRawShape> {
  const shape: Record<string, ZodTypeAny> = {};
  for (const [name, field] of Object.entries(schema.shape) as [string, ZodTypeAny][]) {
    if (REQUIRED_IN_ACCOUNT_DEPLOY.has(name)) {
      // Required here even where the shared schema leaves it optional (TEMPORAL_API_KEY, say).
      shape[name] = field instanceof z.ZodOptional ? field.unwrap() : field;
    } else {
      shape[name] = field.isOptional() ? field : field.optional();
    }
  }
  return z.object(shape);
}

/** The names a schema requires: no default, no optional. */
export function requiredNames(schema: z.ZodObject<ZodRawShape>): string[] {
  return Object.entries(schema.shape)
    .filter(([, field]) => !(field as ZodTypeAny).isOptional())
    .map(([name]) => name);
}
